import { datadogLogs } from "@datadog/browser-logs";
import gql from "graphql-tag";
import _ from "lodash";
import { useCookies } from "vue3-cookies";
import {
  API_ENTITY_INFORMATION,
  API_RELATIONSHIPS,
  CONNECTAIQ_STORAGE_KEY,
  IMPERSONATION_COOKIE_NAME,
  PERMISSIONS,
} from "@/constants";
import { getApiClient } from "@/services/API";
import enrollmentService from "@/services/enrollment";
import entitiesService from "@/services/entities.js";
import formService from "@/services/form";
import profileService from "@/services/profile";
import programSessionService from "@/services/programSessions";
import userService from "@/services/user";
import {
  getStoredTenantContextId,
  isAuthorizedTenant,
  setLocalStorageSelectedEntityId,
  syncSelectedEntityFromLocalStorage,
} from "@/store/utils/caiqStorageSync";
import { clearCookie } from "@/util/cookieMonitor";
import { readCookie, trailingSlash } from "../mixins/helpers";

const { cookies } = useCookies();

const programApiUrlBase = trailingSlash(import.meta.env.VITE_PROGRAM_SERVICE_API_ENDPOINT);
const enrollmentApiUrlBase = trailingSlash(import.meta.env.VITE_ENROLLMENT_SERVICE_ENDPOINT);
const currentEnvironment = determineEnvironment();
const programChangeIds = {
  staging: "605d19f9008126bbd63758a1",
  production: "605dfb7d70debe7013810a76",
  development: "601d8adca37fc749bf1921de",
}; // Program Changes form Ids

const paymentIds = {
  production: ["5f4686082f068b84c96f8db2", "5e4d93df6c740bfc00b07525"],
  staging: ["5f4684a8c9230479b6179900", "5f0e1603870c11607ed8e141"],
  development: ["5f4553626640264a813a5fb1", "5ee7a436e9c4fd073f07bc47"],
};

const programSelectionIds = {
  production: "5e4d6b7993920a7732410c8d",
  staging: "5f0e1603870c115f00d8e146",
  development: "5ee7a436e9c4fd694207bc46",
};

const topLevelDomain = window.location.hostname.split(".").slice(-2).join(".");

const actions = {
  savePlace({ commit }, placeDetails) {
    commit("SET_PLACE_DETAILS", placeDetails);
  },
  async refreshUserDataFromServer({ dispatch, commit }) {
    try {
      const user = await userService.getCurrentUser();
      commit("setCurrentUser", user);
    } catch (error) {
      dispatch("logout", { sessionExpired: true, redirect: true });
    }
  },
  prepareFormio({ commit }) {
    commit("setFormioToken");
  },
  async getEnrollmentData({ commit, state }) {
    // 1. Setting up fetch
    const endpoint = enrollmentApiUrlBase + "progress";
    const headers = new Headers();
    headers.append("x-jwt-token", state.formioToken);
    // 2. Fetch Call for enrollment service
    const fetchCall = await fetch(endpoint, { headers: headers });
    if (fetchCall.status === 200) {
      const info = await fetchCall.json();
      if (Array.isArray(info)) {
        // 3. Set enrollment applications
        commit("setEnrollmentApplicationData", info);
      }
    }
  },
  removeFormioLocalStorage() {
    localStorage.removeItem("formioUser");
    localStorage.removeItem("formioToken");
    cookies.remove("student_portal_remove_local", "/", window.location.hostname.split(".").slice(-2).join("."));
  },
  getFormioSubmissions({ commit }) {
    const formPaths = [
      "programselection",
      "paymentform",
      "institutionpayment",
      "studentapplicationstage2step1",
      "studentapplicationstage2step2",
      "hostuniversityinternship",
      "housing",
      "programfeespaymentoptions",
      "applicationreceived",
      "programchanges",
    ];

    return formService.getSubmissionReport({ formPaths }).then((submissions) => {
      commit("setStudentFormioSubmissions", submissions);
    });
  },
  async getStudentApplications({ commit, state, getters }) {
    const isParticipant = getters.getPermissions.includes(PERMISSIONS.PARTICIPANTS);
    const isImpersonating = getters.isImpersonating;
    const isEmployee =
      getters.getPermissions.includes(PERMISSIONS.EMPLOYEE) ||
      getters.getPermissions.includes(PERMISSIONS.API_EMPLOYEE_STUDENT_VIEW);
    // Action is going to continue only if the users is a student or if the user is Impersonating
    if ((!isParticipant && !isImpersonating) || (isEmployee && !isImpersonating)) return;

    const applications = (await getApiClient().get("/applications")).data;

    applications.forEach((application, index, allApplications) => {
      //Search enrollment application to save the status
      const enrollmentStatus = state.enrollmentApplications.find((app) => app.application_id === application.id);
      application.enrollmentStatus = enrollmentStatus ? enrollmentStatus.enrollment_status : "";

      // next check if they have pending program changes
      const submissionProgramChange = application.submissions.find((submission) => {
        return submission.form === programChangeIds[currentEnvironment];
      });
      if (submissionProgramChange !== undefined) {
        allApplications[index]["programChange"]["requestedChange"] = true;
        allApplications[index]["programChange"]["changeIsPending"] =
          !Object.hasOwn(submissionProgramChange["data"], "changeFinished") &&
          !submissionProgramChange["data"]["changeFinished"];
        allApplications[index]["programChange"]["data"] = submissionProgramChange;

        if (Object.hasOwn(submissionProgramChange["data"], "newApplicationId")) {
          allApplications[index]["programChange"]["isNewProgram"] =
            allApplications[index]["id"] === submissionProgramChange.data.newApplicationId;

          allApplications[index]["programChange"]["isNewProgramSelected"] =
            !!submissionProgramChange.data.newApplicationId;
        }
      }

      //check if the application was paid
      application.applicationPaid = application.submissions.some((submission) => {
        return paymentIds[currentEnvironment].includes(submission.form);
      });

      //Set UI version
      const submissionProgramSelection = application.submissions.find((submission) => {
        return submission.form === programSelectionIds[currentEnvironment];
      });
      if (submissionProgramSelection !== undefined) {
        if (import.meta.env.VITE_UI_VERSION_FLAG && import.meta.env.VITE_UI_VERSION_FLAG.toLowerCase() === "true") {
          application.uiVersion = submissionProgramSelection.data.uiVersion ?? "v1";
        }

        //Set bulk Enrollment data
        application.bulkEnrollmentId = submissionProgramSelection.data.bulkEnrollmentId ?? null;
        application.bulkEnrollmentStatus = submissionProgramSelection.data.enrollmentStatus ?? "";
      }
    });

    commit("setStudentApplications", applications);
  },
  getCurrentApplicationSubmissions({ commit, state }) {
    commit("setCurrentApplicationSubmissions", {});
    if (state.currentApplicationId !== "") {
      return formService
        .getSubmissionReport({
          applicationIds: [state.currentApplicationId],
        })
        .then((submissions) => {
          commit(
            "setCurrentApplicationSubmissions",
            submissions.reduce((report, submission) => {
              // organize submissions into an object indexed by form path
              const formPath = submission.formPath;
              if (!Object.hasOwn(report, formPath)) {
                report[formPath] = [];
              }
              report[formPath].push(submission);
              return report;
            }, {}),
          );
        });
    }
  },
  async getProgramData({ commit, state }, apolloClient) {
    await apolloClient
      .query({
        query: gql`
          query programSessionBySalesforceId($salesforce_id: String!) {
            programSessionBySalesforceId(salesforce_id: $salesforce_id) {
              program_participation_criteria
              program_status
              statement_of_purpose
              visa_required
              salesforce_id
              program {
                name
                internship
                salesforce_id
                customized
                url
                sponsoring_institution_ope8_id
                university {
                  salesforce_id
                  name
                  image
                  url
                }
              }
              form_rules
              post_acceptance_form_rules
              housing_rules
              application_deadline
              application_extension_deadline
              enrollment_deadline
              program_manager
              program_manager_email
              program_manager_image
              minimum_gpa
              deadline
              price
              application_fee
              site {
                salesforce_id
                city
                country
              }
              term
              session
              start_date
              start_date_is_final
              end_date
              end_date_is_final
              academic_details_pdf
              supplemental_form_one
              supplemental_form_two
              supplemental_form_three
              supplemental_form_four
              supplemental_form_instructions
              supplemental_link_one
              supplemental_link_two
              supplemental_link_three
              supplemental_link_four
              cancellation_policy_type
              link_to_custom_cancelation_policy
            }
          }
        `,
        variables: {
          salesforce_id: state.selectedProgramId,
        },
      })
      .then((response) => {
        commit("setProgram", response.data.programSessionBySalesforceId);
        commit("setProgramSelected", true);
        commit("setIsInternship", response.data.programSessionBySalesforceId.program.internship);
        commit(
          "setUniversityName",
          response.data.programSessionBySalesforceId.program.university
            ? response.data.programSessionBySalesforceId.program.university.name
            : null,
        );
        commit(
          "setUniversityCity",
          response.data.programSessionBySalesforceId.program.university
            ? response.data.programSessionBySalesforceId.site.city
            : null,
        );
        commit(
          "setUniversityImageUrl",
          response.data.programSessionBySalesforceId.program.university
            ? response.data.programSessionBySalesforceId.program.university.image
            : null,
        );
        commit(
          "setUniversityWebsiteUrl",
          response.data.programSessionBySalesforceId.program.university
            ? response.data.programSessionBySalesforceId.program.university.url
            : null,
        );
        commit("setSelectedProgramId", state.selectedProgramId);
        commit("setTerm", response.data.programSessionBySalesforceId.term);
      });
  },
  getBillingRules({ commit, state }) {
    const ope8Id = state.currentUser?.university?.value;

    // bail if current user is not associated with a university
    if (!ope8Id) {
      commit("setBillingRules", {});
      return;
    }

    // pad zeros to the beginning until the university id is 8 characters long,
    // since some universities from learning-service database lack leading zeros
    const universityId = String(ope8Id).padStart(8, 0);

    enrollmentService
      .fetchBillingRules(universityId)
      .then((data) => {
        commit("setBillingRules", data);
      })
      .catch(() => {
        commit("setBillingRules", {});
      });
  },
  cacheFormioUser({ commit, state }) {
    // Fetch user if we don't already have user data OR if it's not in localStorage, b/c other places rely on that
    if (!state.userData || Object.keys(state.userData).length === 0) {
      return formService.currentFormServiceUser().then((formioUser) => {
        localStorage.setItem("formioUser", JSON.stringify(formioUser));
        commit("setFormioUser", formioUser);
      });
    }
  },
  async userSetup({ dispatch }) {
    await dispatch("cacheFormioUser");
    dispatch("getBillingRules");
  },
  async getPayments({ commit, state }, applicationId) {
    const response = await fetch(enrollmentApiUrlBase + "student", {
      headers: {
        "Content-type": "application/json",
        "x-jwt-token": state.formioToken,
      },
    });

    const status = await response.status;
    if (status !== 200) {
      return false;
    }

    const data = await response.json();

    //set customer ID/experience ID
    if (data.customer_id) {
      commit("setCustomerId", data.customer_id);
    } else {
      console.log("customer_id does not exist!");
    }

    const payments = [];
    data.progress.forEach((progress) => {
      if (progress.application_id === applicationId && typeof progress.payments !== "undefined") {
        progress.payments.forEach((payment) => {
          payments.push({
            id: payment.id,
            name: payment.name,
            balance: payment.balance,
            due_date: payment.due_date,
            payment_date: payment.payment_date,
            paid_by: payment.paid_by,
            status: payment.status,
            invoice: payment.invoice,
            sku: payment.product_sku,
            line_item_id: payment.opportunity,
            paid: payment.paid,
            salesforce_id: payment.salesforce_id,
          });
        });
      }
    });
    commit("setPayments", payments);
  },
  async setProgramDataByApplicationId({ commit, dispatch, state }, { applicationId, apolloClient }) {
    const selectedProgramId = await fetchProgramSessionIdByApplicationId(applicationId, state);
    if (selectedProgramId === null) {
      console.log("Error setting program data");
      return;
    }
    commit("setSelectedProgramId", selectedProgramId);
    return dispatch("getProgramData", apolloClient);
  },
  initialSetUp({ commit, dispatch }) {
    commit("setFormioToken");
    commit("setSelectedProgramId", "");
    dispatch("userSetup");
  },
  signIn({ state, dispatch, commit }, { apolloClient, submission }) {
    //formioUser key not created by formio on firefox or safari
    if (localStorage.getItem("formioUser") === null) {
      localStorage.setItem("formioUser", JSON.stringify(submission));
    }

    const applicationId = localStorage.getItem("expiredApplicationId");
    //If the redirect url use a selected program set the data
    if (state.sessionExpired && applicationId) {
      dispatch("setProgramDataByApplicationId", {
        applicationId: applicationId,
        apolloClient: apolloClient,
      });
    }

    //Clear redirect variables
    localStorage.removeItem("expiredApplicationId");
    commit("setRedirectPath", "");
    commit("setSessionExpired", false);
  },
  async enrollmentAuth({ commit }) {
    localStorage.removeItem("enrollment_auth");
    const response = await fetch("/enrollment-service/authenticate", {
      method: "POST",
      headers: {
        Accept: "application/json",
        "X-XSRF-Token": readCookie("XSRF-TOKEN"),
      },
    });
    const data = await response.json();
    const token = data.token;
    localStorage.setItem("enrollment_auth", token);
    commit("setEnrollmentToken", token);
  },
  logout({ commit }, { sessionExpired = false, redirectPath = "", reload }) {
    const resetPayload = {
      featureFlags: {},
    };

    commit("setCurrentUser", null);
    commit("university/logout");
    commit("reset", resetPayload);
    commit("setSessionExpired", sessionExpired);
    commit("setEnrollmentToken", "");
    commit("configurator/RESET_CURRENT_ORDER");
    commit("programManager/setSelectedEntityView", {});

    if (typeof Storage !== "undefined") {
      localStorage.removeItem("formioToken");
      localStorage.removeItem("formioUser");
      localStorage.removeItem("enrollment_auth");
      localStorage.removeItem(CONNECTAIQ_STORAGE_KEY);
    }
    cookies.remove("authSessionExpired", "/", topLevelDomain);
    // Clear the shared impersonation session on logout (TRD §3.3 / AC #5).
    clearCookie(IMPERSONATION_COOKIE_NAME, "/", topLevelDomain);

    if (reload) {
      if (sessionExpired) commit("setRedirectPath", redirectPath);
      window.location.href = "/logout/okta";
    }
  },
  async exitImpersonation({ commit }) {
    try {
      const { currentUser } = await userService.exitImpersonation();

      clearCookie(IMPERSONATION_COOKIE_NAME, "/", topLevelDomain);

      if (currentUser) commit("setCurrentUser", currentUser);
      commit("reset");

      return currentUser;
    } catch (error) {
      datadogLogs.logger.error("Failed exiting impersonation", error);
      throw error;
    }
  },
  async getUiVersion({ commit, state }, applicationId) {
    let version = "v1";
    if (
      import.meta.env.VITE_UI_VERSION_FLAG &&
      import.meta.env.VITE_UI_VERSION_FLAG.toLowerCase() === "true" &&
      state.currentUser
    ) {
      await formService
        .listSubmissions("programselection", {
          "data.application_id": applicationId,
          sort: "-created",
          limit: 1,
        })
        .then((submissions) => {
          if (submissions.length) {
            version = submissions[0]?.data?.uiVersion ?? version;
          }
        });
    }
    commit("setUiVersion", version);
  },
  async directProgram({ commit }, programSession) {
    //clear the state custom program data
    commit("setDirectProgramData", {});
    //get program data
    await fetch(programApiUrlBase + "session/" + programSession)
      .then((response) => {
        if (response.ok) {
          return response.json();
        } else {
          throw new Error("Program not found");
        }
      })
      .then((json) => {
        //set state data if custom program
        const obj = {
          salesforce_id: programSession,
          programName: json.program.name,
          customized: json.program.customized,
        };
        commit("setDirectProgramData", obj);
      })
      .catch((error) => {
        console.log(error);
      });
  },
  async getCustomerId({ commit, state }) {
    const response = await fetch(enrollmentApiUrlBase + "student", {
      headers: {
        "Content-type": "application/json",
        "x-jwt-token": state.formioToken,
      },
    });

    const status = await response.status;
    if (status !== 200) {
      return false;
    }

    const data = await response.json();

    //set customer ID/experience ID
    if (data.customer_id) {
      commit("setCustomerId", data.customer_id);
    } else {
      console.log("customer_id does not exist!");
    }
  },
  async getProfile({ commit, dispatch }, oktaId) {
    const profileData = await profileService.getProfile(oktaId);
    if (profileData?.data?.data) {
      commit("setProfileData", profileData.data.data);
      commit("setCurrentGpa", profileData.data.data?.cumulative_gpa ?? 0);
      await dispatch("fetchEntityRelationshipWithAPI");
      return;
    }
    commit("setProfileData", {});
  },
  async getV3Steps({ commit, state, dispatch }, applicationId) {
    // Gets completion data of each step and the program session data in Learning Service
    const stepData = await getApiClient().post(`/steps-status-v3/${applicationId}`);
    if (Array.isArray(stepData?.data?.steps)) {
      commit("setStepsV3", stepData.data.steps);
    }
    if (!_.isEmpty(stepData?.data?.order)) {
      dispatch("configurator/setCurrentOrder", stepData.data.order);
    }
    dispatch("configurator/setPastDeadline", Boolean(stepData?.data?.past_deadline));
    dispatch("configurator/setConfiguratorCompleted", Boolean(stepData?.data?.configurator_completed));
    // Gets program session data if we have it in hand and it's different from what we have on state
    // TODO: We will need to change the comparison with salesforce_id later
    const programSessionId = stepData.data?.order?.session_id ?? "";
    if (programSessionId && programSessionId !== state.programSessionDataV3.id) {
      const programData = await programSessionService.getProgramSession({
        id: programSessionId,
      });

      if (!_.isEmpty(programData?.data?.data?.items)) {
        commit("setProgramSessionDataV3", programData.data.data.items);
      }
    }
  },
  RESET_PRESELECTIONS({ commit, dispatch }) {
    // Resets pre-selections made in landing-page
    commit("setCurrentOrderProgram", {});
    dispatch("configurator/setInternship", []);
  },
  updateBrandingStyles({ commit }, payload = {}) {
    const brandingDetails = payload;
    commit("setBrandingDetails", brandingDetails);

    // Remove rather than set when a tenant has no value: setProperty(name, undefined) writes the
    // literal string "undefined", which is invalid at computed-value time. That silently defeats
    // `var(--branding-x, <fallback>)` — the fallback only applies to an *unset* property, so a
    // declaration reading the var resolves to black instead of the intended default.
    const vars = {
      "--branding-primary": brandingDetails.primary_color,
      "--branding-secondary": brandingDetails.secondary_color,
      "--branding-tertiary": brandingDetails.accent_color,
    };
    for (const [name, value] of Object.entries(vars)) {
      if (value) {
        document.documentElement.style.setProperty(name, value);
      } else {
        document.documentElement.style.removeProperty(name);
      }
    }
  },
  async fetchEntityRelationshipWithAPI({ commit, getters }) {
    const gotRelationship = getters.getEntityApiRelationship !== API_RELATIONSHIPS.DEFAULT;
    const isExclusivelyAnAdvisor =
      getters.getPermissions.includes(PERMISSIONS.INSTITUTION) &&
      !getters.getPermissions.includes(PERMISSIONS.API_EMPLOYEE) &&
      !getters.getPermissions.includes(PERMISSIONS.PARTICIPANTS);
    const entityId = getters.getEntitiesFromProfile?.[0];

    if (!entityId && gotRelationship && isExclusivelyAnAdvisor) {
      commit("setAPIRelationshipToCurrentUser", API_RELATIONSHIPS.DEFAULT);
    } else if (!gotRelationship && isExclusivelyAnAdvisor && entityId) {
      try {
        const entityRequest = await entitiesService.getEntityById(entityId);
        commit("setAPIRelationshipToCurrentUser", entityRequest.data?.data?.items?.api_relationship);
      } catch (err) {
        console.error("Error while trying to fetch advisor's", err);
      }
    }
  },
  async setV3ProgramSession({ commit }, programSessionId) {
    let programData = {};
    if (programSessionId) {
      programData = await programSessionService.getProgramSession({
        id: programSessionId,
      });
    }

    if (!_.isEmpty(programData?.data?.data?.items)) {
      commit("setProgramSessionDataV3", programData.data.data.items);
    }
  },
  /**
   * Resolves the active tenant on app boot by reconciling localStorage
   * (the cross-app source of truth shared with ConnectAIQ) against the
   * Vuex store (which may hold a stale value restored from sessionStorage
   * by vuex-persistedstate).
   *
   * Resolution order:
   *  1. localStorage tenant ID, if the user is authorized for it
   *  2. First entity from the user's JWT entity_ids
   *  3. API_ENTITY_INFORMATION.id (hardcoded default)
   *
   * If the resolved ID differs from the current store selection, fetches
   * the full entity from the API and commits it to the store.
   */
  async setSelectedEntityViewOnAppLoad({ getters }) {
    const userEntityIdList = getters.getEntityIds;
    const selectedEntity = getters["programManager/getSelectedEntityView"];
    const localStorageTenantId = getStoredTenantContextId();

    let selectedTenantContextId = null;
    // If selected tenant id exists in local storage and the user is permitted, sync the app state with this tenant context.
    if (localStorageTenantId && isAuthorizedTenant(this, localStorageTenantId)) {
      selectedTenantContextId = localStorageTenantId;
      // Fetch the new incoming entity data and update the vuex store.
      await syncSelectedEntityFromLocalStorage(this, selectedTenantContextId);
    }

    // If there is no tenant context in local storage then default to the selected if exists otherwise set default and store in local storage.
    if (!selectedTenantContextId && selectedEntity?.id) {
      selectedTenantContextId = selectedEntity.id;
      // Don't need to refetch since it's already pulled from vuex store.
      setLocalStorageSelectedEntityId(selectedTenantContextId);
    }

    const defaultTenantContextId = userEntityIdList.length > 0 ? userEntityIdList[0] : API_ENTITY_INFORMATION.id;
    if (!selectedTenantContextId && !selectedEntity?.id) {
      selectedTenantContextId = defaultTenantContextId;
      // Fetch default entity data and set in vuex store.
      await syncSelectedEntityFromLocalStorage(this, selectedTenantContextId);
      setLocalStorageSelectedEntityId(selectedTenantContextId);
    }
  },
};

export default actions;

const fetchProgramSessionIdByApplicationId = (applicationId) => {
  return formService
    .listSubmissions("programselection", {
      select: "data.programSession",
      "data.application_id": applicationId,
    })
    .then((submissions) => {
      return submissions[0]?.data?.programSession;
    })
    .catch(() => {
      console.log("Error fetching program session");
      return null;
    });
};

function determineEnvironment() {
  const env = import.meta.env.VITE_APP_ENV;
  return env === "local" ? "staging" : env;
}
